import {markets} from '../data/marketplace';
import type {Market, Seller} from '../types';

export type PublicSellerReview = {id: string; author: string; rating: number; text: string; date: string};
export type PublicSellerProfile = {
  id: string;
  name: string;
  avatar?: string;
  country: Market;
  province: string;
  city: string;
  bio: string;
  rating: number | null;
  reviewCount: number;
  reviews: PublicSellerReview[];
};

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function publicText(value: unknown, maximum: number) {
  return typeof value === 'string' ? value.trim().slice(0, maximum) : '';
}

export function publicProfileAvatar(value: unknown) {
  if (typeof value !== 'string' || value.length > 2000 || /[\s\\]/.test(value) || [...value].some(character => character.charCodeAt(0) < 32)) return undefined;
  if (/^\/(?!\/)/.test(value)) return value;
  try { return new URL(value).protocol === 'https:' ? value : undefined; } catch { return undefined; }
}

export function isIllustrativeSeller(seller: Pick<Seller, 'id'>, isDemo = false) {
  return isDemo || seller.id.startsWith('demo-');
}

export function publicSellerLocation(country: Market, province?: string) {
  const countryName = markets.find(market => market.id === country)?.name || 'Paese non dichiarato';
  const area = country === 'CH' ? 'Cantone' : country === 'FR' ? 'Dipartimento' : 'Provincia';
  return `${countryName} · ${area}: ${publicText(province, 100) || (country === 'CH' ? 'non dichiarato' : 'non dichiarata')}`;
}

/** Strict public allowlist; private fields in an accidentally broader response are discarded. */
export function normalizePublicSellerProfile(value: unknown, seller: Seller): PublicSellerProfile | null {
  const response = record(value);
  const user = record(response?.user);
  if (!user || typeof user.id !== 'string' || user.id !== seller.id) return null;
  const reviewIds = new Set<string>();
  const reviews: PublicSellerReview[] = [];
  for (const entry of Array.isArray(response?.reviews) ? response.reviews.slice(0, 50) : []) {
    const review = record(entry);
    if (!review || typeof review.id !== 'string' || !review.id || review.id.length > 160 || reviewIds.has(review.id) || typeof review.rating !== 'number' || !Number.isInteger(review.rating) || review.rating < 1 || review.rating > 5) continue;
    reviewIds.add(review.id);
    const rawDate = typeof review.createdAt === 'string' ? review.createdAt : '';
    reviews.push({
      id: review.id,
      author: publicText(review.reviewerName, 80) || 'Utente',
      rating: review.rating,
      text: publicText(review.body, 6000),
      date: rawDate && Number.isFinite(Date.parse(rawDate)) ? new Date(rawDate).toLocaleDateString('it-IT') : '',
    });
  }
  const country = markets.some(market => market.id === user.country) ? user.country as Market : seller.country;
  const reviewCount = typeof user.reviewCount === 'number' && Number.isInteger(user.reviewCount) && user.reviewCount >= 0 && user.reviewCount <= 1000000 ? user.reviewCount : reviews.length;
  const rating = reviewCount > 0 && typeof user.rating === 'number' && Number.isFinite(user.rating) && user.rating >= 1 && user.rating <= 5 ? user.rating : null;
  return {
    id: user.id,
    name: publicText(user.displayName, 80) || publicText(seller.name, 80) || 'Venditore',
    avatar: publicProfileAvatar(user.avatarUrl),
    country,
    province: publicText(user.province, 100),
    city: publicText(user.city, 100),
    bio: publicText(user.bio, 6000),
    rating,
    reviewCount,
    reviews,
  };
}
